import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

// ── Types ──────────────────────────────────────────────────────────────────────
interface Summary {
  active_meters: number;
  total_meters: number;
  total_balance_units: number;
  monthly_revenue_kes: number;
  month: string;
}

interface PaymentTrend {
  month: string;
  total: number;
  count: number;
}

interface Meter {
  id: number;
  serial_number: string;
  unit_number: string;
  property_name: string;
  status: "online" | "offline" | "fault";
  valve_open: boolean;
  latest_reading: { flow_rate: string; cumulative_value: string } | null;
}

interface TokenBalance {
  meter: number;
  serial_number: string;
  balance: string;
  unit_label: string;
}

interface Payment {
  id: number;
  amount: string;
  phone_number: string;
  status: string;
  token_code: string | null;
  units_allocated: string | null;
  created_at: string;
}

interface DailyUsage { date: string; units_consumed: string; }

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat("en-KE", { maximumFractionDigits: 1 }).format(n);

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// ── Metric card (no border) ────────────────────────────────────────────────────
function MetricCard({
  label, value, sub, icon, iconBg, trend,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  iconBg: string;
  trend?: { value: number; label: string };
}) {
  const up = trend && trend.value >= 0;
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
        {trend && (
          <span
            className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full ${
              up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
            }`}
          >
            {up ? (
              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="currentColor">
                <path d="M5 1l4 8H1z" />
              </svg>
            ) : (
              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="currentColor">
                <path d="M5 9L1 1h8z" />
              </svg>
            )}
            {Math.abs(trend.value)}%
          </span>
        )}
      </div>
      <p className="text-[13px] font-medium text-gray-400 mb-0.5">{label}</p>
      <p className="text-[26px] font-bold text-gray-900 leading-tight">{value}</p>
      {sub && <p className="text-[12px] text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ── Custom tooltip ─────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label, prefix = "", suffix = "" }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f2557] text-white text-[12px] px-3 py-2 rounded-xl shadow-xl">
      <p className="font-semibold mb-1 text-sky-200">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? "#fff" }}>
          {p.name}: <span className="font-bold">{prefix}{typeof p.value === "number" ? fmt(p.value) : p.value}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────────
function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-[16px] font-bold text-gray-800">{title}</h2>
      {sub && <p className="text-[12.5px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Skeleton loader ────────────────────────────────────────────────────────────
function Skeleton({ className }: { className: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuthStore();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [trends, setTrends] = useState<PaymentTrend[]>([]);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [balances, setBalances] = useState<TokenBalance[]>([]);
  const [recentPayments, setRecentPayments] = useState<Payment[]>([]);
  const [usageData, setUsageData] = useState<DailyUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/dashboard/"),
      api.get("/analytics/payments/trends/"),
      api.get("/meters/"),
      api.get("/tokens/balances/"),
      api.get("/payments/history/"),
    ]).then(([s, t, m, b, p]) => {
      setSummary(s.data);
      setTrends(
        (t.data ?? []).map((d: any) => ({
          ...d,
          month: new Date(d.month).toLocaleDateString("en-KE", { month: "short", year: "2-digit" }),
        }))
      );
      const mList: Meter[] = m.data.results ?? m.data;
      setMeters(mList);
      setBalances(b.data.results ?? b.data);
      setRecentPayments((p.data.results ?? p.data).slice(0, 6));

      // fetch 30-day usage for the first meter if available
      if (mList.length > 0) {
        api.get(`/analytics/meter/${mList[0].id}/usage/?days=30`).then(({ data }) =>
          setUsageData(data)
        );
      }
    }).finally(() => setLoading(false));
  }, []);

  // Build bar data: token balance per meter
  const balanceBarData = balances
    .map((b) => ({
      name: b.serial_number.slice(-6),
      balance: parseFloat(b.balance),
      label: b.unit_label,
    }))
    .sort((a, b) => b.balance - a.balance)
    .slice(0, 10);

  // 30-day area chart
  const usageAreaData = usageData.map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-KE", { day: "numeric", month: "short" }),
    consumed: parseFloat(d.units_consumed),
  }));

  // Meter status pie
  const onlineCount = meters.filter((m) => m.status === "online").length;
  const offlineCount = meters.filter((m) => m.status === "offline").length;
  const faultCount = meters.filter((m) => m.status === "fault").length;
  const pieData = [
    { name: "Online", value: onlineCount },
    { name: "Offline", value: offlineCount },
    { name: "Fault", value: faultCount },
  ].filter((d) => d.value > 0);
  const PIE_COLORS = ["#10b981", "#94a3b8", "#f43f5e"];

  // Valve status
  const valveOpen = meters.filter((m) => m.valve_open).length;
  const valveClosed = meters.length - valveOpen;

  const statusColors: Record<string, string> = {
    completed: "bg-emerald-50 text-emerald-600",
    pending: "bg-amber-50 text-amber-600",
    failed: "bg-red-50 text-red-500",
    cancelled: "bg-gray-100 text-gray-500",
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 w-72" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-KE", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="space-y-7 pb-8">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-gray-900">
            {greeting()}, {user?.first_name || user?.username}
          </h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{dateStr}</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-2.5 shadow-sm self-start sm:self-auto">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[13px] font-medium text-gray-600">
            {summary?.active_meters ?? 0} meter{(summary?.active_meters ?? 0) !== 1 ? "s" : ""} live
          </span>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard
          label="Active Meters"
          value={`${summary?.active_meters ?? 0} / ${summary?.total_meters ?? 0}`}
          sub="Online right now"
          iconBg="bg-emerald-50"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8} className="w-5 h-5">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
          trend={{ value: 8, label: "vs last month" }}
        />
        <MetricCard
          label="Token Balance"
          value={fmt(summary?.total_balance_units ?? 0)}
          sub="Units across all meters"
          iconBg="bg-sky-50"
          icon={
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
              <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#38bdf8" opacity=".8" />
            </svg>
          }
        />
        <MetricCard
          label="Monthly Revenue"
          value={`KES ${fmt(summary?.monthly_revenue_kes ?? 0)}`}
          sub={summary?.month}
          iconBg="bg-violet-50"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth={1.8} className="w-5 h-5">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" />
            </svg>
          }
          trend={{ value: 12, label: "vs last month" }}
        />
        <MetricCard
          label="Total Meters"
          value={String(summary?.total_meters ?? 0)}
          sub={`${valveOpen} valves open · ${valveClosed} closed`}
          iconBg="bg-amber-50"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={1.8} className="w-5 h-5">
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
        />
      </div>

      {/* ── Row 2: Revenue bar + Meter status pie ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Revenue / transactions bar chart */}
        <div className="bg-white rounded-2xl p-5 shadow-sm lg:col-span-2">
          <SectionHeader
            title="Revenue & Transactions"
            sub="Monthly M-Pesa collections over the past 6 months"
          />
          {trends.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-gray-300 text-sm">No payment data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trends} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<ChartTooltip prefix="KES " />} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="total" name="Revenue (KES)" fill="#0ea5e9" radius={[6, 6, 0, 0]}>
                  {trends.map((_, i) => (
                    <Cell
                      key={i}
                      fill={i === trends.length - 1 ? "#0f2557" : "#bae6fd"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {trends.length > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-[#0f2557]" />
                <span className="text-[12px] text-gray-400">Current month</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-[#bae6fd]" />
                <span className="text-[12px] text-gray-400">Previous months</span>
              </div>
            </div>
          )}
        </div>

        {/* Meter status pie */}
        <div className="bg-white rounded-2xl p-5 shadow-sm flex flex-col">
          <SectionHeader title="Meter Status" sub="Device health overview" />
          {meters.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-300 text-sm">No meters registered</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={70}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {[
                  { label: "Online", count: onlineCount, color: "bg-emerald-400" },
                  { label: "Offline", count: offlineCount, color: "bg-slate-300" },
                  { label: "Fault", count: faultCount, color: "bg-rose-400" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between text-[13px]">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-sm ${row.color}`} />
                      <span className="text-gray-500">{row.label}</span>
                    </div>
                    <span className="font-semibold text-gray-700">
                      {row.count}
                      <span className="text-gray-300 font-normal"> / {meters.length}</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Row 3: Token balance bar + 30-day usage area ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Token balance per meter */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <SectionHeader
            title="Token Balance by Meter"
            sub="Current water units remaining per device"
          />
          {balanceBarData.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-gray-300 text-sm">No balances yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={balanceBarData} layout="vertical" barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#64748b", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                />
                <Tooltip content={<ChartTooltip suffix=" units" />} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="balance" name="Balance" radius={[0, 6, 6, 0]}>
                  {balanceBarData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={
                        entry.balance < 10
                          ? "#fca5a5"
                          : entry.balance < 30
                          ? "#fde68a"
                          : "#6ee7b7"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {balanceBarData.length > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
              {[
                { color: "bg-red-300", label: "< 10 units (critical)" },
                { color: "bg-amber-200", label: "< 30 units (low)" },
                { color: "bg-emerald-300", label: "Healthy" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-sm ${b.color}`} />
                  <span className="text-[11.5px] text-gray-400">{b.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 30-day consumption area */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <SectionHeader
            title="30-Day Consumption"
            sub={meters.length > 0 ? `Meter · ${meters[0]?.serial_number}` : "No meter data"}
          />
          {usageAreaData.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-gray-300 text-sm">No usage data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={usageAreaData}>
                <defs>
                  <linearGradient id="usageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip suffix=" units" />} />
                <Area
                  type="monotone"
                  dataKey="consumed"
                  name="Consumed"
                  stroke="#0ea5e9"
                  strokeWidth={2.5}
                  fill="url(#usageGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: "#0ea5e9", stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Row 4: Recent payments + Valve status + Meter list ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Recent payments */}
        <div className="bg-white rounded-2xl p-5 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <SectionHeader title="Recent Payments" sub="Last 6 M-Pesa transactions" />
            <a
              href="/payments"
              className="text-[12.5px] font-semibold text-sky-600 hover:text-sky-700 transition-colors flex-shrink-0 -mt-5"
            >
              View all →
            </a>
          </div>
          {recentPayments.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-300 text-sm">No payments yet</div>
          ) : (
            <div className="space-y-2">
              {recentPayments.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      p.status === "completed" ? "bg-emerald-50" : "bg-gray-50"
                    }`}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke={p.status === "completed" ? "#10b981" : "#9ca3af"} strokeWidth={1.8} className="w-4 h-4">
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <path d="M2 10h20" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800 truncate">
                      KES {parseFloat(p.amount).toLocaleString()}
                    </p>
                    <p className="text-[11.5px] text-gray-400 truncate">
                      {p.phone_number}
                      {p.token_code && (
                        <> · <code className="font-mono text-sky-600">{p.token_code}</code></>
                      )}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span
                      className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                        statusColors[p.status] ?? "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {p.status}
                    </span>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {new Date(p.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Valve & meter quick-status */}
        <div className="flex flex-col gap-4">

          {/* Valve summary */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <SectionHeader title="Valve Status" sub="Water flow control" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4">
                      <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#38bdf8" opacity=".8" />
                    </svg>
                  </span>
                  <span className="text-[13px] text-gray-600">Flowing</span>
                </div>
                <span className="text-[20px] font-bold text-gray-800">{valveOpen}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-sky-400 to-blue-600 transition-all duration-500"
                  style={{ width: meters.length ? `${(valveOpen / meters.length) * 100}%` : "0%" }}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth={1.8} className="w-4 h-4">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M9 12h6" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="text-[13px] text-gray-600">Closed</span>
                </div>
                <span className="text-[20px] font-bold text-gray-800">{valveClosed}</span>
              </div>
            </div>
          </div>

          {/* Meter list */}
          <div className="bg-white rounded-2xl p-5 shadow-sm flex-1">
            <SectionHeader title="Live Meters" sub="Real-time flow readings" />
            <div className="space-y-2.5 max-h-48 overflow-y-auto">
              {meters.length === 0 ? (
                <p className="text-[13px] text-gray-300">No meters</p>
              ) : (
                meters.slice(0, 6).map((m) => (
                  <div key={m.id} className="flex items-center gap-2.5">
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        m.status === "online"
                          ? "bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.7)]"
                          : m.status === "fault"
                          ? "bg-rose-400"
                          : "bg-gray-300"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12.5px] font-medium text-gray-700 truncate">
                        {m.property_name} · {m.unit_number}
                      </p>
                      <p className="text-[11px] text-gray-400 font-mono">
                        {m.serial_number}
                      </p>
                    </div>
                    <span
                      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
                        m.valve_open ? "bg-sky-50 text-sky-600" : "bg-gray-50 text-gray-400"
                      }`}
                    >
                      {m.valve_open ? "Open" : "Closed"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>

    </div>
  );
}
